import { yupResolver } from '@hookform/resolvers/yup';
import { motion, type Variants } from 'framer-motion';
import { AlertTriangle, Key, Lock, Shield, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import QRCode from 'react-qr-code';
import * as yup from 'yup';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { enable2FA, verify2FASetup } from '../auth-client';
import { BackupCodes } from './BackupCodes';
import { OTPInput } from './OTPInput';

const passwordSchema = yup.object({
  password: yup
    .string()
    .required('Senha é obrigatória para confirmar identidade'),
});

type PasswordFormData = yup.InferType<typeof passwordSchema>;

interface TwoFactorSetupProps {
  tempToken: string;
  onComplete: (user: any) => void;
  onError: (error: string) => void;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' as const },
  },
};

export function TwoFactorSetup({
  tempToken,
  onComplete,
  onError,
}: TwoFactorSetupProps) {
  const [step, setStep] = useState<'password' | 'qr' | 'verify' | 'backup'>(
    'password'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [qrData, setQrData] = useState<{
    totpURI: string;
    backupCodes: string[];
    setupToken: string;
  } | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [completedUser, setCompletedUser] = useState<any>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordFormData>({
    resolver: yupResolver(passwordSchema),
  });

  const handlePasswordSubmit = async (data: PasswordFormData) => {
    setLoading(true);
    setError('');

    try {
      const response = await enable2FA(tempToken, data.password);
      if (response.success && response.data) {
        setQrData({
          totpURI: response.data.totpURI,
          backupCodes: response.data.backupCodes,
          setupToken: response.data.setupToken,
        });
        setStep('qr');
      } else {
        setError(response.message || 'Erro ao configurar 2FA');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao configurar 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (otpCode.length !== 6) {
      setError('Por favor, digite o código de 6 dígitos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await verify2FASetup(
        qrData?.setupToken || tempToken,
        otpCode
      );
      setCompletedUser(response.user);
      setStep('backup');
    } catch (err: any) {
      setError(err.message || 'Código inválido. Verifique e tente novamente.');
      setOtpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodesComplete = () => {
    if (completedUser) {
      onComplete(completedUser);
    }
  };

  useEffect(() => {
    if (step === 'qr' && otpCode.length === 6) {
      setStep('verify');
    }
  }, [step, otpCode.length]);

  const renderPasswordStep = () => (
    <motion.div
      className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 md:p-10"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div variants={itemVariants} className="mb-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(210,35%,32%)] to-[hsl(210,40%,25%)] flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-[hsl(210,50%,10%)] mb-2">
          Configurar 2FA
        </h1>
        <p className="text-[hsl(215,16%,47%)] text-sm">
          A autenticação de dois fatores é obrigatória
        </p>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Alert className="mb-6 border-[hsl(43,56%,61%)] bg-[hsl(43,56%,90%)]">
          <AlertTriangle className="h-4 w-4 text-[hsl(43,56%,40%)]" />
          <AlertDescription className="text-[hsl(43,56%,30%)] text-sm">
            Você precisará de um aplicativo autenticador (Google Authenticator,
            Authy, etc.)
          </AlertDescription>
        </Alert>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-6"
        >
          <Alert variant="destructive" className="border-red-200 bg-red-50">
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        </motion.div>
      )}

      <form onSubmit={handleSubmit(handlePasswordSubmit)} className="space-y-4">
        <motion.div variants={itemVariants} className="space-y-2">
          <Label
            htmlFor="password"
            className="text-[hsl(210,50%,10%)] font-medium text-sm"
          >
            Confirme sua senha
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,16%,47%)]" />
            <Input
              id="password"
              type="password"
              placeholder="Digite sua senha"
              {...register('password')}
              className={`pl-10 h-11 bg-[hsl(210,40%,98%)] border-[hsl(214,32%,91%)] text-[hsl(210,50%,10%)] placeholder:text-[hsl(215,16%,47%)] focus:border-[hsl(43,56%,61%)] focus:ring-[hsl(43,56%,61%)] transition-all ${
                errors.password
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : ''
              }`}
            />
          </div>
          {errors.password && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-red-500"
            >
              {errors.password.message}
            </motion.p>
          )}
        </motion.div>

        <motion.div variants={itemVariants} className="pt-2">
          <Button
            type="submit"
            className="w-full h-11 bg-[hsl(210,35%,32%)] hover:bg-[hsl(210,35%,25%)] text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            disabled={loading}
          >
            {loading ? 'Configurando...' : 'Continuar'}
          </Button>
        </motion.div>
      </form>
    </motion.div>
  );

  const renderQRStep = () => (
    <motion.div
      className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-8 md:p-10"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div variants={itemVariants} className="mb-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(142,76%,36%)] to-[hsl(142,76%,30%)] flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Smartphone className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-[hsl(210,50%,10%)] mb-2">
          Escaneie o QR Code
        </h1>
        <p className="text-[hsl(215,16%,47%)] text-sm">
          Use seu aplicativo autenticador para escanear
        </p>
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="flex justify-center p-6 bg-white rounded-xl border border-[hsl(214,32%,91%)] mb-6"
      >
        {qrData?.totpURI && (
          <QRCode
            value={qrData.totpURI}
            size={180}
            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            viewBox="0 0 256 256"
          />
        )}
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="text-center text-sm text-[hsl(215,16%,47%)] mb-6"
      >
        <p className="font-medium mb-1">Aplicativos suportados:</p>
        <p>Google Authenticator, Authy, Microsoft Authenticator, 1Password</p>
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="pt-6 border-t border-[hsl(214,32%,91%)] space-y-4"
      >
        <p className="text-center text-[hsl(210,50%,10%)] font-medium text-sm">
          Digite o código de 6 dígitos:
        </p>
        <OTPInput
          value={otpCode}
          onChange={setOtpCode}
          error={!!error}
          onComplete={(code) => setOtpCode(code)}
        />

        {error && (
          <Alert variant="destructive" className="border-red-200 bg-red-50">
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        )}

        <Button
          onClick={() => setStep('verify')}
          className="w-full h-11 bg-[hsl(210,35%,32%)] hover:bg-[hsl(210,35%,25%)] text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          disabled={otpCode.length !== 6}
        >
          Verificar Código
        </Button>
      </motion.div>
    </motion.div>
  );

  const renderVerifyStep = () => (
    <motion.div
      className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 md:p-10"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div variants={itemVariants} className="mb-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(43,56%,61%)] to-[hsl(43,56%,50%)] flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Key className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-[hsl(210,50%,10%)] mb-2">
          Verificar Código
        </h1>
        <p className="text-[hsl(215,16%,47%)] text-sm">
          Confirmando seu aplicativo autenticador
        </p>
      </motion.div>

      <motion.div variants={itemVariants} className="text-center mb-6">
        <p className="text-[hsl(215,16%,47%)] text-sm mb-3">Código inserido:</p>
        <div className="text-3xl font-mono font-bold tracking-widest text-[hsl(210,50%,10%)]">
          {otpCode}
        </div>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-6"
        >
          <Alert variant="destructive" className="border-red-200 bg-red-50">
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        </motion.div>
      )}

      <motion.div variants={itemVariants} className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => {
            setStep('qr');
            setOtpCode('');
            setError('');
          }}
          className="flex-1 h-11 border-[hsl(214,32%,91%)] text-[hsl(210,50%,10%)] hover:bg-[hsl(210,40%,98%)]"
          disabled={loading}
        >
          Voltar
        </Button>
        <Button
          onClick={handleVerifyCode}
          className="flex-1 h-11 bg-[hsl(210,35%,32%)] hover:bg-[hsl(210,35%,25%)] text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          disabled={loading}
        >
          {loading ? 'Verificando...' : 'Confirmar'}
        </Button>
      </motion.div>
    </motion.div>
  );

  const renderBackupStep = () =>
    qrData && (
      <BackupCodes
        backupCodes={qrData.backupCodes}
        onComplete={handleBackupCodesComplete}
      />
    );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[hsl(210,35%,32%)] to-[hsl(210,40%,25%)] px-4">
      {step === 'password' && renderPasswordStep()}
      {step === 'qr' && renderQRStep()}
      {step === 'verify' && renderVerifyStep()}
      {step === 'backup' && renderBackupStep()}
    </div>
  );
}
